import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./vender.css";

const FreelancerList = () => {
  const [freelancers, setFreelancers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [limit] = useState(50);
  
  // Filter states
  const [selectedPlan, setSelectedPlan] = useState("All");
  const [selectedPlanPrice, setSelectedPlanPrice] = useState("All");
  const [selectedNextPlan, setSelectedNextPlan] = useState("All");
  const [verifiedFilter, setVerifiedFilter] = useState("All");
  
  // Dropdown data
  const [allCities, setAllCities] = useState([]);
  const [allPincodes, setAllPincodes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planPrices, setPlanPrices] = useState({});
  
  // Search states
  const [searchCity, setSearchCity] = useState("");
  const [searchPincode, setSearchPincode] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [searchShop, setSearchShop] = useState("");
  const [searchVendorId, setSearchVendorId] = useState("");
  const [searchMobile, setSearchMobile] = useState("");
  
  // UI states
  const [showDetails, setShowDetails] = useState({});
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isPincodeOpen, setIsPincodeOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Notification form
  const [type, setType] = useState("Text");
  const [videoUrl, setVideoUrl] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Vendor data cache
  const [vendorData, setVendorData] = useState({});
  
  // Refs
  const cityDropdownRef = useRef(null);
  const pincodeDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const shopDropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const [filters, setFilters] = useState({
    city: "",
    pincode: "",
    shopName: "",
    daysLeft: "",
    category: "",
    startDate: "",
    expiryDate: "",
    vendorId: "",
    mobile: "",
    createdFrom: "",
    createdTo: "",
  });

  const VALID_NEXT_PLANS = ["Gold", "Silver", "Platinum"];

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch("https://visitguru.in/api/v1/plans/price");
        const data = await res.json();
        if (data.success) {
          setPlans(data.data);
          const planMap = {};
          data.data.forEach(plan => {
            const key = plan.name;
            if (!planMap[key]) planMap[key] = [];
            planMap[key].push(`${plan.discountedPrice} (updated)`);
          });
          setPlanPrices(planMap);
        }
      } catch (error) {
        console.error("Error fetching plans:", error.message);
      }
    };
    fetchPlans();
  }, []);

  // Fetch dropdown options (only once)
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [citiesRes, categoriesRes] = await Promise.all([
          axios.get("https://visitguru.in/api/v1/city", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("https://visitguru.in/api/v1/category", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (citiesRes.data.success) {
          const cities = citiesRes.data.data.map(c => c.cityName);
          setAllCities(["All", ...cities]);
        }

        if (categoriesRes.data.success) {
          const categories = categoriesRes.data.data.map(c => c.categoryName);
          setAllCategories(["All", ...categories]);
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };
    fetchDropdownData();
  }, []);

  // Debounced fetch function
  const debouncedFetch = useCallback((page, filterParams) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchFreelancers(page, filterParams);
    }, 500); // 500ms debounce
  }, []);

  // Main fetch function with filters
  const fetchFreelancers = async (page = 1, filterParams = {}) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      setLoading(true);

      // Build query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filterParams,
        planName: selectedPlan !== "All" ? selectedPlan : "",
        planPrice: selectedPlanPrice !== "All" ? selectedPlanPrice : "",
        nextPlan: selectedNextPlan !== "All" ? selectedNextPlan : "",
        verifiedFilter: verifiedFilter !== "All" ? verifiedFilter : "",
      });

      const res = await axios.get(
        `https://visitguru.in/api/v1/subscription/freelancers/plans?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000, // 30 second timeout
        }
      );

      if (res.data.success) {
        const freelancerData = res.data.data || [];
        const pagination = res.data.pagination || {};

        setFreelancers(freelancerData);
        setTotalPages(pagination.totalPages || 1);
        setTotalRecords(pagination.totalRecords || 0);
        setFilteredRecords(pagination.filteredRecords || freelancerData.length);

        // Extract unique pincodes from current page
        if (page === 1) {
          const pincodes = new Set(
            freelancerData.map((f) => String(f.pincode)).filter(Boolean)
          );
          setAllPincodes(["All", ...Array.from(pincodes)]);
        }
      } else {
        toast.error("Failed to load freelancers.");
      }
    } catch (err) {
      console.error("Error fetching freelancers:", err);
      if (err.code === 'ECONNABORTED') {
        toast.error("Request timeout. Please try again.");
      } else {
        toast.error("Error fetching freelancers: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchFreelancers(1, filters);
  }, []);

  // Apply filters with debounce
  useEffect(() => {
    debouncedFetch(1, filters);
    setCurrentPage(1);
  }, [
    selectedPlan,
    selectedPlanPrice,
    selectedNextPlan,
    verifiedFilter,
    filters,
  ]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchFreelancers(newPage, filters);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => handlePageChange(Math.max(currentPage - 1, 1));
  const handleNext = () => handlePageChange(Math.min(currentPage + 1, totalPages));

  // Fetch completed tasks for vendor (with caching)
  const fetchCompletedTasksForVendor = async (vendorId) => {
    // Return cached data if available
    if (vendorData[vendorId]) {
      return vendorData[vendorId];
    }

    try {
      const res = await fetch(
        "https://visitguru.in/api/v1/bookservice/completedTasklist",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shopID: vendorId }),
        }
      );
      const data = await res.json();
      if (data.success) {
        const result = {
          tasks: Array.isArray(data.data) ? data.data : [],
          completedTasksCount: data.totalCompletedTasks || 0,
          completedTasksTotalEarning: data.completedEarnings || 0,
        };
        
        // Cache the result
        setVendorData(prev => ({ ...prev, [vendorId]: result }));
        return result;
      }
      return { tasks: [], completedTasksCount: 0, completedTasksTotalEarning: 0 };
    } catch (error) {
      console.error(`Task fetch failed for vendor ${vendorId}:`, error);
      return { tasks: [], completedTasksCount: 0, completedTasksTotalEarning: 0 };
    }
  };

  const handleToggleDetails = async (id) => {
    setShowDetails(prev => ({ ...prev, [id]: !prev[id] }));
    if (!showDetails[id] && !vendorData[id]) {
      await fetchCompletedTasksForVendor(id);
    }
  };

  const getPlanPrice = (planName) => {
    if (!planName || plans.length === 0) return "0";
    const planParts = planName.split(" ");
    const baseName = planParts[0];
    const price = planParts.length > 1 ? parseInt(planParts[1]) : null;
    const planObj = plans.find(p => p.name === baseName && (!price || p.discountedPrice === price));
    return planObj ? planObj.discountedPrice : "0";
  };



  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setIsCityOpen(false);
      }
      if (pincodeDropdownRef.current && !pincodeDropdownRef.current.contains(event.target)) {
        setIsPincodeOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(event.target)) {
        setIsShopOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter helpers
  const filteredCities = allCities.filter(city => 
    city.toLowerCase().includes(searchCity.toLowerCase())
  );

  const filteredPincodes = allPincodes.filter(p => 
    p.toString().toLowerCase().includes(searchPincode.toLowerCase())
  );

  const filteredCategories = allCategories.filter(cat => 
    cat.toLowerCase().includes(searchCategory.toLowerCase())
  );

  const filteredShops = freelancers
    .filter(f => f.shopName?.toLowerCase().includes(searchShop.toLowerCase()))
    .map(f => ({ value: f.shopName, label: f.shopName }));

  const onCityChange = (city) => {
    setFilters(prev => ({ ...prev, city: city === "All" ? "" : city }));
    setIsCityOpen(false);
    setSearchCity("");
  };

  const onPincodeChange = (pincode) => {
    setFilters(prev => ({ ...prev, pincode: pincode === "All" ? "" : pincode }));
    setIsPincodeOpen(false);
    setSearchPincode("");
  };

  const onCategoryChange = (category) => {
    setFilters(prev => ({ ...prev, category: category === "All" ? "" : category }));
    setIsCategoryOpen(false);
    setSearchCategory("");
  };

  const handleVendorIdChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, vendorId: value }));
    setSearchVendorId(value);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, mobile: value }));
    setSearchMobile(value);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFontSize(14);
    doc.text("Freelancer List", 14, 15);

    const tableColumn = [
      "#", "Vendor ID", "Name", "Shop Name", "Mobile", "City", "Pincode",
      "Plan", "Days Left", "Status", "Next Plan", "Total Tasks", "Completed Tasks",
      "Total Earnings", "Total Employees", "Categories"
    ];

    const tableRows = freelancers.map((f, i) => {
      const planName = f.currentPlan?.planName || "Free";
      const planPrice = f.currentPlan?.discountedPrice || getPlanPrice(planName);
      const status = f.currentPlan?.isActive ? "Active" : "Inactive";

      return [
        (currentPage - 1) * limit + i + 1,
        f.vendorID || "N/A",
        `${f.firstName || ""} ${f.lastName || ""}`.trim() || "N/A",
        f.shopName || "N/A",
        f.mobile || "N/A",
        f.city?.[0] || "N/A",
        f.pincode || "N/A",
        `${planName} ${planPrice}`,
        f.currentPlan?.daysLeft || 0,
        status,
        f.nextPlan || "-",
        f.totalTask || 0,
        f.completedTask || 0,
        `₹${f.totalEarning || 0}`,
        f.totalEmployee || 0,
        f.category?.join(", ") || "N/A",
      ];
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [40, 116, 166] },
    });
    doc.save(`Freelancers_Page_${currentPage}.pdf`);
  };

  // Notification handlers
  const validateUrl = (url) => {
    try { new URL(url); return true; } catch { return false; }
  };

  const handleSelectFreelancer = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? freelancers.map(f => f._id) : []);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title || !message || !type || selectedIds.length === 0) {
      toast.error("Please fill all required fields and select at least one vendor.");
      return;
    }
    if (type === "Video" && (!videoUrl || !validateUrl(videoUrl))) {
      toast.error("Please enter a valid video URL.");
      return;
    }

    const payload = { title, body: message, type, selectedIds };
    if (type === "Video") payload.videoUrl = videoUrl;

    try {
      const response = await axios.post(
        "https://visitguru.in/api/v1/notification/notify",
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        toast.success(`Notification sent to ${response.data.history?.totalSent || selectedIds.length} vendor(s)!`);
        setShowForm(false);
        setTitle("");
        setMessage("");
        setVideoUrl("");
        setType("Text");
        setSelectedIds([]);
      } else {
        toast.error(`Failed: ${response.data.message}`);
      }
    } catch (err) {
      toast.error(`Error: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="px-4 py-3 border-b">
        <h2 className="font-semibold text-base text-blueGray-700 mb-2">
          Freelancers 
          <span className="ml-2 text-sm text-blueGray-500">
            (Showing: {freelancers.length} | Filtered: {filteredRecords} | Total: {totalRecords})
          </span>
          <button 
            onClick={exportToPDF} 
            className="float-right bg-lightBlue-600 text-white px-4 py-2 rounded hover:bg-lightBlue-700 transition"
          >
            Download PDF
          </button>
        </h2>

        {/* FILTERS */}
        <div className="flex flex-wrap gap-4 mb-4">
          {/* Plan Filter */}
          <div className="w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={selectedPlan}
              onChange={(e) => {
                setSelectedPlan(e.target.value);
                setSelectedPlanPrice("All");
              }}
              className="w-full px-3 py-1 border rounded text-sm"
              style={{ height: "38px" }}
            >
              <option value="All">All Plans</option>
              {Object.keys(planPrices).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Plan Price Filter */}
          {selectedPlan !== "All" && selectedPlan !== "Free" && selectedPlan !== "SilverFree" && (
            <div className="w-1/5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {selectedPlan} Prices
              </label>
              <select
                value={selectedPlanPrice}
                onChange={(e) => setSelectedPlanPrice(e.target.value)}
                className="w-full px-3 py-1 border rounded text-sm"
                style={{ height: "38px" }}
              >
                <option value="All">All Prices</option>
                {planPrices[selectedPlan]?.map((price, index) => (
                  <option key={index} value={price}>{price}</option>
                ))}
              </select>
            </div>
          )}

          {/* Next Plan Filter */}
          <div className="w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Next Plan</label>
            <select 
              value={selectedNextPlan} 
              onChange={(e) => setSelectedNextPlan(e.target.value)} 
              className="w-full px-3 py-1 border rounded text-sm" 
              style={{ height: "38px" }}
            >
              <option value="All">All Next Plans</option>
              {VALID_NEXT_PLANS.map(plan => (
                <option key={plan} value={plan}>{plan}</option>
              ))}
            </select>
          </div>

          {/* Created From */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
            <input
              type="date"
              value={filters.createdFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, createdFrom: e.target.value }))}
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* Created To */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
            <input
              type="date"
              value={filters.createdTo}
              onChange={(e) => setFilters(prev => ({ ...prev, createdTo: e.target.value }))}
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* City Dropdown */}
          <div className="relative w-1/5" ref={cityDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">City/Village</label>
            <div
              className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer hover:bg-gray-50 transition"
              style={{ height: "38px", display: "flex", alignItems: "center" }}
              onClick={() => {
                setIsCityOpen(!isCityOpen);
                setIsPincodeOpen(false);
                setIsCategoryOpen(false);
                setIsShopOpen(false);
              }}
            >
              {filters.city || "Select City"}
            </div>
            {isCityOpen && (
              <div className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <input
                  type="text"
                  placeholder="Search city..."
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none sticky top-0 bg-white"
                />
                <ul>
                  <li 
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold" 
                    onClick={() => onCityChange("All")}
                  >
                    All Cities
                  </li>
                  {filteredCities.length > 0 ? (
                    filteredCities.map((city, index) => (
                      <li 
                        key={index} 
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer" 
                        onClick={() => onCityChange(city)}
                      >
                        {city}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500">No cities found</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Pincode Dropdown */}
          <div className="relative w-1/5" ref={pincodeDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
            <div
              className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer hover:bg-gray-50 transition"
              style={{ height: "38px", display: "flex", alignItems: "center" }}
              onClick={() => {
                setIsPincodeOpen(!isPincodeOpen);
                setIsCityOpen(false);
                setIsCategoryOpen(false);
                setIsShopOpen(false);
              }}
            >
              {filters.pincode || "Select Pincode"}
            </div>
            {isPincodeOpen && (
              <div className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <input
                  type="text"
                  placeholder="Search pincode..."
                  value={searchPincode}
                  onChange={(e) => setSearchPincode(e.target.value)}
                  className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none sticky top-0 bg-white"
                />
                <ul>
                  <li 
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold" 
                    onClick={() => onPincodeChange("All")}
                  >
                    All Pincodes
                  </li>
                  {filteredPincodes.length > 0 ? (
                    filteredPincodes.map((pincode, index) => (
                      <li 
                        key={index} 
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer" 
                        onClick={() => onPincodeChange(pincode)}
                      >
                        {pincode}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500">No pincodes found</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Category Dropdown */}
          <div className="relative w-1/5" ref={categoryDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <div
              className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer hover:bg-gray-50 transition"
              style={{ height: "38px", display: "flex", alignItems: "center" }}
              onClick={() => {
                setIsCategoryOpen(!isCategoryOpen);
                setIsCityOpen(false);
                setIsPincodeOpen(false);
                setIsShopOpen(false);
              }}
            >
              {filters.category || "Select Category"}
            </div>
            {isCategoryOpen && (
              <div className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <input
                  type="text"
                  placeholder="Search category..."
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none sticky top-0 bg-white"
                />
                <ul>
                  <li 
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold" 
                    onClick={() => onCategoryChange("All")}
                  >
                    All Categories
                  </li>
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((category, index) => (
                      <li 
                        key={index} 
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer" 
                        onClick={() => onCategoryChange(category)}
                      >
                        {category}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500">No categories found</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Shop Name Dropdown */}
          <div className="relative w-1/5" ref={shopDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shop Name</label>
            <div
              className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer hover:bg-gray-50 transition"
              style={{ height: "38px", display: "flex", alignItems: "center" }}
              onClick={() => {
                setIsShopOpen(!isShopOpen);
                setIsCityOpen(false);
                setIsPincodeOpen(false);
                setIsCategoryOpen(false);
              }}
            >
              {filters.shopName || "Select Shop"}
            </div>
            {isShopOpen && (
              <div className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <input
                  type="text"
                  placeholder="Search shop..."
                  value={searchShop}
                  onChange={(e) => setSearchShop(e.target.value)}
                  className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none sticky top-0 bg-white"
                />
                <ul>
                  <li 
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold" 
                    onClick={() => {
                      setFilters(prev => ({ ...prev, shopName: "" }));
                      setIsShopOpen(false);
                      setSearchShop("");
                    }}
                  >
                    All Shops
                  </li>
                  {filteredShops.length > 0 ? (
                    filteredShops.map((shop) => (
                      <li 
                        key={shop.value} 
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer" 
                        onClick={() => {
                          setFilters(prev => ({ ...prev, shopName: shop.value }));
                          setIsShopOpen(false);
                          setSearchShop("");
                        }}
                      >
                        {shop.label}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500">No shops found</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Vendor ID */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor ID</label>
            <input
              type="text"
              value={searchVendorId}
              onChange={handleVendorIdChange}
              placeholder="Enter Vendor ID"
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* Mobile No */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No</label>
            <input
              type="text"
              value={searchMobile}
              onChange={handleMobileChange}
              placeholder="Enter Mobile No"
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* From Date */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* To Date */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.expiryDate}
              onChange={(e) => setFilters(prev => ({ ...prev, expiryDate: e.target.value }))}
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* Days Left */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Days Left</label>
            <input
              type="number"
              placeholder="Days Left"
              min="0"
              value={filters.daysLeft}
              onChange={(e) => setFilters(prev => ({ ...prev, daysLeft: e.target.value }))}
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            />
          </div>

          {/* Status Filter */}
          <div className="relative w-1/5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
              className="w-full px-3 py-1 border border-blueGray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: "38px" }}
            >
              <option value="All">All</option>
              <option value="Verified">Verified</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* TABLE SECTION */}
      <div className="space-y-6">
        <div className="bg-white rounded shadow">
          <div className="bg-blue-600 text-white p-3 font-semibold flex justify-between items-center" style={{ color: "black" }}>
            <span>Total Freelancers ({filteredRecords})</span>
            <button
              onClick={() => setShowForm(prev => !prev)}
              style={{
                backgroundColor: "#0884c4",
                width: "100px",
                height: "35px",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              {showForm ? "Close" : "Send"}
            </button>

            {/* NOTIFICATION FORM */}
            {showForm && (
              <div className="absolute z-20 bg-white border border-blueGray-200 mt-2 w-80 rounded shadow-lg p-4 right-4 top-16">
                <form onSubmit={handleSend}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-blueGray-700 mb-1">Notification Title</label>
                    <input
                      type="text"
                      className="w-full border border-blueGray-200 rounded text-sm px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
                      placeholder="Enter notification title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-black mb-1">Notification Type</label>
                    <select
                      className="w-full border border-blueGray-200 rounded text-sm px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
                      value={type}
                      onChange={(e) => {
                        setType(e.target.value);
                        setVideoUrl("");
                      }}
                      required
                    >
                      <option value="Text">Text</option>
                      <option value="Video">Video</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-black mb-1">Message</label>
                    <textarea
                      rows={4}
                      className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 outline-none text-black"
                      placeholder="Type your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>
                  {type === "Video" && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-black mb-1">Video URL</label>
                      <input
                        type="url"
                        className="w-full border border-black rounded text-sm px-3 py-1 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/video.mp4"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-blueGray-700 mb-1">
                      Selected Vendors ({selectedIds.length})
                    </label>
                    <p className="text-sm text-gray-500">
                      {selectedIds.length > 0 ? `${selectedIds.length} vendor(s) selected` : "No vendors selected"}
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="bg-gray-200 text-blueGray-700 px-3 py-1 rounded text-sm hover:bg-gray-300"
                      onClick={() => {
                        setShowForm(false);
                        setTitle("");
                        setVideoUrl("");
                        setMessage("");
                        setType("Text");
                        setSelectedIds([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        backgroundColor: "#0884c4",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        border: "none"
                      }}
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border border-gray-200">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="px-3 py-2 border">#</th>
                    <th className="px-3 py-2 border">Vendor ID</th>
                    <th className="px-3 py-2 border">Avatar</th>
                    <th className="px-3 py-2 border">Name</th>
                    <th className="px-3 py-2 border">City/Village</th>
                    <th className="px-3 py-2 border">Plan</th>
                    <th className="px-3 py-2 border">Days Left</th>
                    <th className="px-3 py-2 border">Next Plan</th>
                    <th className="px-3 py-2 border">Actions</th>
                    <th className="px-3 py-2 border">
                      <input
                        type="checkbox"
                        style={{
                          width: "18px",
                          height: "18px",
                          accentColor: "#11ADA3",
                          cursor: "pointer",
                        }}
                        checked={freelancers.length > 0 && freelancers.every(f => selectedIds.includes(f._id))}
                        onChange={handleSelectAll}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center p-8">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                          <p className="text-gray-600">Loading freelancers...</p>
                        </div>
                      </td>
                    </tr>
                  ) : freelancers.length > 0 ? (
                    freelancers.map((f, index) => {
                      const currentPlan = f.currentPlan || { name: "Free", daysLeft: 0 };
                      const planDisplayName = `${currentPlan.planName || currentPlan.name || "Free"} ${currentPlan.discountedPrice || getPlanPrice(currentPlan.planName || currentPlan.name)}`;
                      
                      return (
                        <React.Fragment key={f._id}>
                          <tr className="border-b hover:bg-gray-50 transition">
                            <td className="px-3 py-2 border">{(currentPage - 1) * limit + index + 1}</td>
                            <td className="px-3 py-2 border">{f.vendorID || "N/A"}</td>
                            <td className="px-3 py-2 border">
                              <img
                                src={`https://ui-avatars.com/api/?name=${f.firstName || "User"}&background=random`}
                                alt="avatar"
                                className="w-8 h-8 rounded-full"
                              />
                            </td>
                            <td className="px-3 py-2 border">{f.firstName} {f.lastName}</td>
                            <td className="px-3 py-2 border">{f.city?.[0] || "-"}</td>
                            <td className="px-3 py-2 border">{planDisplayName}</td>
                            <td className="px-3 py-2 border">{currentPlan.daysLeft || 0}</td>
                            <td className="px-3 py-2 border">{f.nextPlan || "-"}</td>
                            <td className="px-3 py-2 border">
                              <button
                                onClick={() => handleToggleDetails(f._id)}
                                style={{"backgroundColor": "#0884c4"}}
                                className="bg-[#0884c4] text-white px-3 py-1 rounded w-20 text-xs hover:bg-[#06679a] transition"
                              >
                                {showDetails[f._id] ? "HIDE" : "VIEW" }
                              </button>
                            </td>
                            <td className="px-3 py-2 border">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(f._id)}
                                onChange={() => handleSelectFreelancer(f._id)}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  accentColor: "#11ADA3",
                                  cursor: "pointer",
                                }}
                              />
                            </td>
                          </tr>

                          {/* EXPANDED DETAILS ROW */}
                          {showDetails[f._id] && (
                            <tr>
                              <td colSpan="10" className="bg-gray-50 text-xs px-3 py-2">
                                <div className="space-y-1">
                                  <p><strong>Vendor ID:</strong> {f.vendorID || "N/A"}</p>
                                  <p><strong>User:</strong> {f.firstName} {f.lastName}</p>
                                  <p><strong>Mobile:</strong> {f.mobile || "N/A"}</p>
                                  <p><strong>Address:</strong> {f.address || "-"}</p>
                                  <p><strong>Pincode:</strong> {f.pincode || "-"}</p>
                                  <p><strong>Email:</strong> {f.email || "-"}</p>
                                  <p><strong>Shop Name:</strong> {f.shopName || "-"}</p>
                                  <p><strong>City/Village:</strong> {f.city?.join(", ") || "-"}</p>
                                  <p><strong>Price:</strong> ₹{currentPlan.discountedPrice || getPlanPrice(currentPlan.planName)}</p>
                                  <p><strong>Category:</strong> {f.category?.join(", ") || "-"}</p>
                                  <p><strong>Subscription:</strong> {planDisplayName}</p>
                                  <p><strong>Next Plan:</strong> {f.nextPlan || "None"}</p>
                                  <p><strong>Start Date:</strong> {currentPlan.startDate ? new Date(currentPlan.startDate).toLocaleDateString() : "-"}</p>
                                  <p><strong>Expiry Date:</strong> {currentPlan.expiryDate ? new Date(currentPlan.expiryDate).toLocaleDateString() : "-"}</p>
                                  <p><strong>Plan Status:</strong> {f.planStatus?.charAt(0).toUpperCase() + f.planStatus?.slice(1) || "Unknown"}</p>
                                  <p><strong>Days Left:</strong> {currentPlan.daysLeft || 0}</p>
                                  <p><strong>Total Tasks:</strong> {f.totalTask || 0}</p>
                                  <p><strong>Completed Tasks:</strong> {f.completedTask || vendorData[f._id]?.completedTasksCount || 0}</p>
                                  <p><strong>Total Earnings:</strong> ₹{f.totalEarning || vendorData[f._id]?.completedTasksTotalEarning || 0}</p>
                                  <p><strong>Total Employees:</strong> {f.totalEmployee || 0}</p>
                                  <p><strong>Created At:</strong> {new Date(f.createdAt).toLocaleString()}</p>
                                  <p><strong>Updated At:</strong> {f.updatedAt ? new Date(f.updatedAt).toLocaleString() : "-"}</p>

                                  {/* Employee & Plan History Tables */}
                                  <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                                    {/* Employee Table */}
                                    <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: "4px", overflow: "hidden" }}>
                                      <div style={{ backgroundColor: "#f3f4f6", padding: "8px 16px", fontWeight: "600", fontSize: "12px" }}>
                                        Employees
                                      </div>
                                      <table className="min-w-full text-sm">
                                        <thead className="bg-blueGray-100">
                                          <tr>
                                            <th className="px-4 py-2 border-b text-left">No.</th>
                                            <th className="px-4 py-2 border-b text-left">Name</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {Array.isArray(f.employeeNames) && f.employeeNames.length > 0 ? (
                                            f.employeeNames.map((name, idx) => (
                                              <tr key={idx}>
                                                <td className="px-4 py-2 border-b">{idx + 1}</td>
                                                <td className="px-4 py-2 border-b">{name}</td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan="2" className="px-4 py-2 text-center">N/A</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>

                                    {/* Plan History Table */}
                                    <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: "4px", overflow: "hidden" }}>
                                      <div style={{ backgroundColor: "#f3f4f6", padding: "8px 16px", fontWeight: "600", fontSize: "12px" }}>
                                        Expired Plans
                                      </div>
                                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                        <table className="min-w-full text-xs">
                                          <thead className="bg-gray-100 sticky top-0">
                                            <tr>
                                              <th className="px-2 py-1 border-b text-left">Plan</th>
                                              <th className="px-2 py-1 border-b text-left">Price</th>
                                              <th className="px-2 py-1 border-b text-left">Start</th>
                                              <th className="px-2 py-1 border-b text-left">End</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {Array.isArray(f.expiredPlans) && f.expiredPlans.length > 0 ? (
                                              f.expiredPlans.map((plan, idx) => (
                                                <tr key={idx} className="bg-red-50">
                                                  <td className="px-2 py-1 border-b">{plan.planName || plan.name}</td>
                                                  <td className="px-2 py-1 border-b">₹{plan.discountedPrice || 0}</td>
                                                  <td className="px-2 py-1 border-b">
                                                    {plan.startDate ? new Date(plan.startDate).toLocaleDateString() : "-"}
                                                  </td>
                                                  <td className="px-2 py-1 border-b">
                                                    {plan.expiryDate ? new Date(plan.expiryDate).toLocaleDateString() : "-"}
                                                  </td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr>
                                                <td colSpan="4" className="px-2 py-1 text-center text-gray-500">
                                                  No expired plans
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center p-4 text-gray-400">
                        No freelancers found for selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION CONTROLS */}
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
              <button
                onClick={handlePrev}
                disabled={currentPage === 1 || loading}
                className="bg-gray-200 px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition flex items-center gap-2"
              >
                {loading && currentPage > 1 && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                )}
                ← Previous
              </button>
              
              <div className="flex items-center gap-2">
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
                <span className="text-sm">
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </span>
              </div>

              <button
                onClick={handleNext}
                disabled={currentPage === totalPages || loading}
                className="bg-gray-200 px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition flex items-center gap-2"
              >
                Next →
                {loading && currentPage < totalPages && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                )}
              </button>
            </div>

            {/* Page Jump */}
            <div className="flex justify-center items-center mt-2 gap-2">
              <span className="text-sm text-gray-600">Go to page:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page);
                  }
                }}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerList;